import { CheckCircle, CircleOutlined } from "@mui/icons-material";
import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { ProjectTask } from "../../../../../../interfaces/Projects/Catalogs/projectTasks";
import GenericPromises from "../../../../../../api/GenericPromises";
import useSnackBar from "../../../../../../components/Commons/SnackBar/useSnackBar";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../../../../components/Commons/Spinner/Spinner";
import { useDates } from "../../../../../../hooks/useDates";
import { DialogEntity } from "../../../../../../components/Dialogs/DialogEntity";
import { Header } from "../../../../../../components/Header";
import { ConfirmDialog } from "../../../../ProjectTaskDashboard/components/dialogs/confirmDialog";
import { format, parseISO } from "date-fns";
import { useProjectTasksStore } from "../../../../../../store/Projects/ProjectDetails/ProjectTasksStore";
import { useTaskStatesStore } from "../../../../../../store/Projects/TaskStates/TaskStatesStore";
import { UpdateProjectTask } from "../ProjectTask/update";
import { Menuitem } from "../../../../../../interfaces/Security/menu";
import { FieldType } from "../../../../../../interfaces/Projects/Catalogs/fieldTypes";
import { AuthContext } from "../../../../../../context/AuthContext";
import { useProjectStagesStore } from "../../../../../../store/Projects/ProjectDetails/ProjectStagesStore";
import { ProjectStage } from "../../../../../../interfaces/Projects/Catalogs/projectStages";

interface TaskItemProps {
  project_id: number | undefined;
  resourceScreen: Menuitem;
  task: ProjectTask;
  color: number[] | undefined;
  isEdit: boolean;
  comboFieldTypes: FieldType[];
}

export const TaskItem = ({
  project_id,
  resourceScreen,
  task,
  color,
  isEdit,
  comboFieldTypes
}: TaskItemProps) => {
  const [t] = useTranslation("global");
  const [loadingPut, setLoadingPut] = useState(false);
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const { GenericGetResource, GenericPutResource } = GenericPromises();
  const [dataloaded, setDataLoaded] = useState(false);
  const { TimeConverter } = useDates();
  const [lastUpdateUser, setLastUpdateUser] = useState<string>();
  const [lastUpdateDate, setLastUpdateDate] = useState<string>();
  const [dialogConfirmUncheckTask, setDialogConfirmUncheckTask] = useState(false);
  const [taskColor, setTaskColor] = useState<string>();
  const allTaskCompleted = useProjectTasksStore(store => store.verifyAllTaskCompleted);
  const taskStates = useTaskStatesStore(store => store.taskStates);
  const updateTaskInStage = useProjectTasksStore(store => store.updateTaskInStage)
  const updateProjectStage = useProjectStagesStore(store => store.updateProjectStage);
  const [openDialogUpdateTask, setOpenDialogUpdateTask] = useState(false);
  const { user } = useContext(AuthContext);

  const storeChecked = useProjectTasksStore(store => {
    if (task.project_task_id) {
      return store.isTaskCompleted[task.project_task_id] ?? false
    }
  })

  const fetchLastTaskState = async () => {
    GenericGetResource(`/projecttaskstates/lastprojecttaskstatebyprojecttaskid/${task.project_task_id}`)
      .then((response) => {
        if (response.data.data.length > 0) {
          const lastState = response.data.data[0];
          setLastUpdateUser(lastState.last_update_user);
          setLastUpdateDate(lastState.last_update_date);
          return;
        }
        setLastUpdateUser(task.last_update_user);
        setLastUpdateDate(task.last_update_date?.toString());
      })
      .catch((error) => {
        showSnackBar(error.message, "error");
      })
      .finally(() => {
        setDataLoaded(true)

      })
  };

  const formatDates = (date: string) => {
    const dateConverted = TimeConverter(date.toString())
    return dateConverted ? format(dateConverted, 'dd/MM/yyyy HH:mm:ss') : ' ';
  }


  const handleTaskCompletion = () => {
    const currentState = taskStates.find(
      (state) => state.task_state_id === task.current_task_state_id
    );
    const nextState = taskStates.find(
      (state) => state.task_state_name === (storeChecked ? "PENDING" : "DONE")
    );
    if (nextState?.task_state_id && currentState?.task_state_id) {
      setLoadingPut(true);
      const myData = {
        new_task_state_id: nextState.task_state_id,
        project_task_id: task.project_task_id,
        group_id: user?.group_id,
        last_update_user: user?.user_email
      }
      GenericPutResource('/projecttask/validatetaskandstage', myData)
        .then((response) => {
          const responseData = response.data;

          if (task.project_stage_id && responseData[0].projecttasks) {
            updateTaskInStage(task.project_stage_id, responseData[0].projecttasks[0]);
          }

          if (responseData[1].projectstages && responseData[1].projectstages.length > 0) {
            responseData[1].projectstages.forEach((projectStage: ProjectStage) => {
              console.log(projectStage)
              updateProjectStage(projectStage);
            });
          }
        })
        .catch((error) => {
          if (error.response.data.message) {
            showSnackBar(t(error.response.data.message), "info");
          } else {
            showSnackBar(error.message, "error");
          }
        })
        .finally(() => setLoadingPut(false));
    }
  };

  useEffect(() => {
    setDataLoaded(false)
    setTaskColor(task.project_task_color);
    fetchLastTaskState();
  }, [task]);

  const taskItemStyles = {
    border: `2px solid ${taskColor}`,
    borderRadius: `8px`,
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
    backgroundColor: color
      ? `rgba(${color[0] - 20}, ${color[1] - 20}, ${color[2]}, 0.2)`
      : "rgba(0, 0, 0, 0.1)",
    "&:hover": {
      backgroundColor: color
        ? `rgba(${color[0] - 20}, ${color[1] - 20}, ${color[2]}, 0.09)`
        : "rgba(0, 0, 0, 0.1)",
    },
    display: "flex",
    alignItems: "center",
    padding: "8px",
    transition: "all 0.3s ease",
  };

  return (
    <>
      {!dataloaded && (
        <Grid item  xs={12}>
          <Skeleton variant="rounded" />
          </Grid>
      )}
      {dataloaded && (
        <Grid item key={task.project_task_id} xs={12}>
          <Box
            sx={taskItemStyles}
            onClick={() => {
              setOpenDialogUpdateTask(true);
            }}
          >
            <FormControlLabel
              sx={{ marginLeft: 0.1 }}
              control={
                <Checkbox
                  checked={storeChecked}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (storeChecked) {
                      setOpenDialogUpdateTask(false);
                      setDialogConfirmUncheckTask(true);
                      return
                    }
                    handleTaskCompletion();
                    setOpenDialogUpdateTask(false);
                    // verifyAllTaskCompleted();
                  }}
                  icon={<CircleOutlined />}
                  checkedIcon={<CheckCircle />}
                  sx={{
                    color: "primary.main",
                    "&:hover": {
                      color: "primary.main",
                    },
                    "&.Mui-checked": {
                      color: "primary.main",
                    },
                  }}
                />
              }
              label={<></>}
              disabled={loadingPut}
            />
            <div>
              <div className="d-flex"
                style={{ alignItems: "end" }}>
                <Typography
                  style={{
                    textDecoration: storeChecked ? "line-through" : "none",
                    color: storeChecked ? "gray" : "inherit",
                    marginRight: "8px",
                  }}
                >
                  {task.project_task_name}
                </Typography>
                <span
                  style={{
                    marginRight: "8px",
                    color: "gray",
                  }}
                >
                  -
                </span>
                {lastUpdateUser && lastUpdateDate && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {' '}  {`${lastUpdateUser}`} {lastUpdateDate && formatDates(lastUpdateDate)}. {' '}
                  </Typography>
                )}
              </div>
              {lastUpdateUser && lastUpdateDate && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {t("projectsdetails.projecttaskdetails.start_date")}: {task.project_task_started_date_evaluated ? task.project_task_started_date_evaluated.toString() : ' '}
                  <br />
                  {t("projectsdetails.projecttaskdetails.due_date")}: {task.project_task_due_date_evaluated ? task.project_task_due_date_evaluated.toString() : ' '}
                </Typography>
              )}
            </div>
          </Box>
        </Grid>
      )}
      <DialogEntity
        open={dialogConfirmUncheckTask}
        title={<Header title={t("projecttasks.title-view")} />}
        maxWidth={"sm"}
        fullWidth
        content={
          <ConfirmDialog
            projectTaskName={task.project_task_name}
            isEdit={isEdit}
            loadingPut={loadingPut}
            setDialogConfirmUncheckTask={setDialogConfirmUncheckTask}
            handleTaskCompletion={handleTaskCompletion}
          />
        }
      />
      <DialogEntity
        content={
          <UpdateProjectTask
            permissions={resourceScreen}
            setOpenDialogUpdateTask={setOpenDialogUpdateTask}
            project_id={project_id}
            projectTaskPayload={task}
            comboFieldTypes={comboFieldTypes}
          />
        }
        open={openDialogUpdateTask}
        setOpen={setOpenDialogUpdateTask}
        title={
          <Header
            title={t("projecttasks.title-view")}
            size="sm"
          />
        }
      />
      <SnackbarComponent />
    </>
  );
};
